// Adds one event to the signed-in agent's own Google Calendar from the browser (no server, no stored tokens).
// Uses Google Identity Services token popup with the calendar.events scope, then POST events.insert.
// Needs: REACT_APP_GOOGLE_CLIENT_ID (OAuth Web client; add the site + localhost as Authorized JavaScript origins,
// and Calendar API enabled). Until the consent screen is verified, users must be listed as test users.
import { GOOGLE_CLIENT_ID, GOOGLE_SCOPES } from '../config/google';

export const CALENDAR_ENABLED = Boolean(GOOGLE_CLIENT_ID);

export function buildEvent({ title, start, minutes = 60, name, phone, type, timeZone = 'America/New_York' }) {
  const s = new Date(start); const e = new Date(s.getTime() + (Number(minutes) || 60) * 60000);
  const description = [name && `Client: ${name}`, phone && `Phone: ${phone}`, type && `Type: ${type}`, 'Logged in Resi'].filter(Boolean).join('\n');
  return { summary: title || `${type || 'Appointment'}: ${name || 'client'}`, description, start: { dateTime: s.toISOString(), timeZone }, end: { dateTime: e.toISOString(), timeZone } };
}

export const loadGis = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.oauth2) return resolve(window.google);
  const el = document.createElement('script'); el.src = 'https://accounts.google.com/gsi/client';
  el.onload = () => resolve(window.google); el.onerror = () => reject(new Error('Could not load Google sign-in.'));
  document.head.appendChild(el);
});

export const googleErrorText = (code) => ({
  popup_closed: 'the Google sign-in window was closed before finishing.',
  popup_failed_to_open: 'the Google sign-in popup was blocked. Allow popups for this site and try again.',
  access_denied: 'Google Calendar access was not allowed.',
  timeout: 'Google did not respond in time (popup blocked, closed, or this site is not an authorized origin).',
}[code] || String(code));

// Rejects if p does not settle within ms, so the UI can never hang on Google.
export const withTimeout = (p, ms, code = 'timeout') => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(new Error(googleErrorText(code))), ms);
  Promise.resolve(p).then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
});

let cached = null; // { token, exp } in memory only
const getToken = async () => {
  if (cached && cached.exp > Date.now()) return cached.token;
  const google = await loadGis();
  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({ client_id: GOOGLE_CLIENT_ID, scope: GOOGLE_SCOPES.calendar,
      callback: (r) => (r.error ? reject(new Error(googleErrorText(r.error))) : (cached = { token: r.access_token, exp: Date.now() + (r.expires_in - 60) * 1000 }, resolve(r.access_token))),
      // Without this, a closed/blocked popup or an origin_mismatch never calls back and the caller waits forever.
      error_callback: (err) => reject(new Error(googleErrorText(err?.type || err?.message || 'popup_failed'))) });
    client.requestAccessToken();
  });
};

export async function addCalendarEvent(eventInput, { timeoutMs = 90000 } = {}) {
  if (!CALENDAR_ENABLED) throw new Error('Google Calendar is not set up (REACT_APP_GOOGLE_CLIENT_ID).');
  const token = await withTimeout(getToken(), timeoutMs);
  const res = await withTimeout(fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(buildEvent(eventInput)),
  }), 20000);
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error?.message || `Google Calendar HTTP ${res.status}`);
  return { id: d.id, link: d.htmlLink };
}
